import { FormImage, NewImage } from "src/store/useUploadProduct/FormImagesSlice/formImagesTypes";
import { getEnv } from "../EnvironmentTools";
import { ImageInfo, SignedUrlBodyRequestData, SignedUrlResponse } from "./model/upload-image.model";
import { OpenapiSingleton } from "../OpenapiSingleton";
import { useShop } from "src/hooks/Utils/Shop/useShop";

const RETRY_DELAY = 500;
const RETRY_NUMBER = 4;
const DELAY_POOLING = 1000;
const APPLICATION_NAME = "Zara Pre-owned Resell";

interface ImageData {
  contentLength: number, 
  assetId: string,
  index: number,
  indexFile: number,
}

export interface FilesData {
  image: FormImage; 
  index: number
}

export const UploadImageErrorTypes = {
  Signing: 'SIGNING',
  Uploading: 'Uploading',
  Pooling: 'POOLING',
} as const;
export type UploadImageErrorTypes = typeof UploadImageErrorTypes[keyof typeof UploadImageErrorTypes];

export async function uploadImagesArray(files: FilesData[]): Promise<{ deliveryUrl?: string, assetId?: String, reason?: any, indexFile?: number }[]> {
  const filesArray = Array.from(files);
  const uploadableFiles = filesArray.filter((file) => isImageUploadable((file?.image?.content as NewImage).file));
  const uploadPromises = uploadableFiles.map((file, index) => uploadImage((file.image?.content as NewImage).file, file.index, index));
  const sortedImages = Promise.all(uploadPromises).then(images => images.sort((img1, img2) => img2?.contentLength - img1?.contentLength)).catch((reason) => [reason]);
  await delay(DELAY_POOLING)
  let poolingImages = [];
  for (const image of await sortedImages) {
    const images = await getPoolingImages(image);
    if (images) { 
      const newPoolingImages = [...poolingImages];
      newPoolingImages[image?.index] = images;
      poolingImages = newPoolingImages;
    }
    else break;
  }
  const response = await Promise.all(poolingImages).catch((reason) => [reason]);
  return response;
}

function isImageUploadable(file: File) {
  const mimeType = getMimeType(file);
  return mimeType === "image/jpeg" || mimeType === "image/jpg" || mimeType === "image/png"
}

export async function createGetSignedUrlBody(imageInfo: ImageInfo): Promise<SignedUrlBodyRequestData> {
  const { file, contentType, contentLength, serviceAccount } = imageInfo;
  const originalAssetName = file.name;
  return {
    optimize: false,
    application: APPLICATION_NAME,
    metadata: {
      partnumber: "1234567890",
      brand: "Zara",
    },
    contentType,
    contentLength,
    originalAssetName,
    serviceAccount,
    visibility: "PUBLIC",
    tenant: "ZARA"
  }
}

export function getUploadHeaders( imageInfo: ImageInfo, signedUrlResponse: SignedUrlResponse, ): Record<string, string> {
  const { url, assetId } = signedUrlResponse;
  const { file, contentType, contentLength, serviceAccount } = imageInfo;
  const headers = {
    "Content-Type": contentType,
    "x-goog-content-length-range": `${contentLength},${contentLength}`,
    "x-goog-meta-sa": serviceAccount,
    "x-goog-meta-application": APPLICATION_NAME,
    "x-goog-meta-version": "2",
    "x-goog-meta-assetid": assetId,
    "x-goog-meta-originalassetname": file?.name,
    "x-goog-meta-tenant": "ZARA",
    "x-goog-meta-visibility": "PUBLIC",
    googleSignedUrl: url,
  };
  return headers;
}


export async function uploadImage(file: File, indexFile: number, index: number): Promise<{ contentLength: number, assetId: string, index: number, indexFile: number }> {
  const imageInfo = await getImageInfo(file);
  const signedUrlResponse = await OpenapiSingleton.AmandaApiInstance.signedURL(imageInfo);
  const { assetId } = await OpenapiSingleton.AmandaApiInstance.uploadSignedURL(imageInfo, signedUrlResponse);
  const {contentLength} = imageInfo;
  return { contentLength, assetId, index, indexFile };
}

async function getPoolingImages(image: ImageData): Promise<{ deliveryUrl: string, assetId: string, indexFile: number }> {
  const { assetId, indexFile } = image;
  const deliveryUrl = await poolingForDeliveryUrl(assetId);
  if (deliveryUrl) {
    return { deliveryUrl, assetId, indexFile };
  } else {
    return await Promise.reject(new Error(UploadImageErrorTypes.Pooling));
  }
}

async function getImageInfo(file: File): Promise<ImageInfo> {
  const contentType = getMimeType(file);
  const contentLength = file.size;
  const { serviceAccount } = getEnv();
  return { file, contentType, contentLength, serviceAccount };
}

function getMimeType(file: File) {
  return file?.type;
}

function delay(t: number): Promise<void> {
  return new Promise((resolve) => {
    setTimeout(() => resolve(), t);
  })
}
async function poolingForDeliveryUrl(assetId: string): Promise<string> {
  let deliveryURL = "";
  let counter = 0;
  while (!deliveryURL && counter < RETRY_NUMBER) {
    await delay(RETRY_DELAY)
    deliveryURL = await getDeliveryUrl(assetId);
    counter++
  }
  return deliveryURL;
}

export async function getDeliveryUrl(assetId: string): Promise<string> {
  const { shopLang, shopMarketId } = useShop();

  try {
    const response = await OpenapiSingleton.AmandaServiceManagementAPIApi.getImageDataV2(
        assetId,
        shopMarketId,
        shopLang,
      ).catch((err) => "");
      
    if (typeof response === "string") {
      return "";
    } else {
      return response.data?.deliveryURL;
    }
  } catch (error) {
    return "";
  }
}

